import React from 'react';
import { Select } from '@planne-software/uni';
import { Typography } from '@planne-software/uni/mui/material';

import { useTranslate } from '@src/core/hooks';

import { SequentialSlotShell } from '../SequentialSlotShell';
import { useAttributeSelector } from '../../hooks/useAttributeSelector';
import { useGuardedSequentialOpenChange } from '../../hooks/useGuardedSequentialOpenChange';
import { attributeSlot } from '../../types/productBookingSlot';
import type { AttributeOption, TariffGroupAttributeType } from '../../types/attribute';

type AttributeSelectorProps = {
  type: TariffGroupAttributeType;
};

const AttributeSelector = ({ type }: AttributeSelectorProps) => {
  const { i18n } = useTranslate();
  const state = useAttributeSelector(type);

  const slot = attributeSlot(type);
  const guardedOnOpenChange = useGuardedSequentialOpenChange(slot, state.onOpenChange);

  if (!state.visible) {
    return null;
  }

  return (
    <SequentialSlotShell slot={slot}>
      <Select<AttributeOption>
        name={`product-booking-${type}`}
        id={`product-booking-${type}`}
        fullWidth
        size='small'
        options={state.options}
        value={state.selectedOption ?? undefined}
        getOptionLabel={(option) => option.label}
        getOptionKey={(option) => option.value}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        disabled={state.loading || state.isAddingToCart}
        loading={state.loading}
        open={state.open}
        onOpen={() => guardedOnOpenChange(true)}
        onClose={() => guardedOnOpenChange(false)}
        onChange={(_, option) => state.onSelect((option as AttributeOption | null) ?? null)}
        aria-label={state.label}
        noOptionsText={i18n('product_booking_attribute_no_option_found')}
        loadingText={i18n('product_booking_attribute_loading')}
        preview={
          <Typography variant='text-sm-semibold'>
            {state.selectedValue ??
              i18n('product_booking_attribute_placeholder', { attribute: state.label.toLocaleLowerCase() })}
          </Typography>
        }
      />
    </SequentialSlotShell>
  );
};

export default AttributeSelector;
